import AfterLoginSection from "@/components/marketing/AfterLoginSection";
import type { LandingDemoReplay } from "@/components/marketing/HeroDemoPlayer";
import HowItWorksSection from "@/components/marketing/HowItWorksSection";
import {
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  LightningIcon,
} from "@/components/marketing/MarketingIcons";
import SampleEvaluationSection from "@/components/marketing/SampleEvaluationSection";
import SectionHeader from "@/components/marketing/SectionHeader";
import WeeklyRecheckSection from "@/components/marketing/WeeklyRecheckSection";

type MarketingSectionsProps = {
  appBaseUrl: string;
  landingDemo: LandingDemoReplay;
};

type PricingFeature = {
  icon: "check" | "check-circle" | "clock" | "lightning";
  iconBg: string;
  subtitle?: string;
  text: string;
};

const freeFeatures: PricingFeature[] = [
  {
    icon: "check",
    iconBg: "bg-green-500",
    text: "1 product",
    subtitle: "3 lifetime page audits",
  },
  {
    icon: "check",
    iconBg: "bg-green-500",
    text: "No commitment",
    subtitle: "Get results in minutes",
  },
];

const monitoringFeatures: PricingFeature[] = [
  {
    icon: "check",
    iconBg: "bg-green-500",
    text: "Journeys after they log in",
    subtitle: "We sign in with a test account you provide",
  },
  {
    icon: "check",
    iconBg: "bg-green-500",
    text: "Pages and journeys re-checked every week",
    subtitle: "A page or journey run uses one audit from your budget",
  },
];

const monitoringCapacityOptions = [
  { websites: "1 domain", pages: "40 audits / month", price: "$30" },
  {
    websites: "Unlimited domains",
    pages: "120 audits / month",
    price: "$90",
  },
];

const faqs = [
  {
    question: "What do I need to get started?",
    answer:
      "A product URL. That’s enough for the public sample. Journeys after login need a test account you provide, on a paid plan.",
  },
  {
    question: "Do you test after people sign in?",
    answer: "Yes. With a test account you provide, we sign in, go through key journeys, and flag friction.",
  },
  {
    question: "How does weekly monitoring work?",
    answer:
      "After your initial audit, weekly monitoring rechecks your monitored pages and journeys and shows what\u2019s new, fixed, and still open. Each recheck uses one audit from your plan budget.",
  },
  {
    question: "What kind of issues do you find?",
    answer:
      "We look for friction: steps that are confusing, harder than they should be, or leave no clear next action.",
  },
  {
    question: "Does ScoutUX replace user research?",
    answer:
      "No. ScoutUX flags task-level usability risks; user research helps you learn what real people need, think, and feel.",
  },
  {
    question: "Can I cancel my subscription anytime?",
    answer:
      "Yes, you can cancel your plan at any time. There are no long-term commitments or cancellation fees. Your access will continue until the end of your current billing period, and you can always restart your subscription when needed.",
  },
];

export default function MarketingSections({ appBaseUrl, landingDemo }: MarketingSectionsProps) {
  return (
    <>
      <HowItWorksSection />
      <SampleEvaluationSection replay={landingDemo} />
      <AfterLoginSection />
      <WeeklyRecheckSection />
      <PricingSection appBaseUrl={appBaseUrl} />
      <FaqSection appBaseUrl={appBaseUrl} />
      <MarketingFooter />
    </>
  );
}

function PricingSection({ appBaseUrl }: Pick<MarketingSectionsProps, "appBaseUrl">) {
  return (
    <section id="pricing" className="bg-white py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          title="Simple, transparent pricing"
          description="Start free on public pages. Pay to cover the product after login."
        />

        <div className="mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)]">
          <PricingCard
            name="Free"
            price="$0"
            description="Public pages only. See how an audit looks."
            features={freeFeatures}
            buttonLabel="Try for free"
            buttonClass="from-gray-600 to-gray-700 hover:shadow-gray-500/25"
            hoverClass="from-gray-700 to-gray-800"
            appBaseUrl={appBaseUrl}
          />
          <PricingCard
            name="Pro"
            description="Journeys after they log in, plus public pages. We sign in with a test account you provide."
            capacityOptions={monitoringCapacityOptions}
            features={monitoringFeatures}
            buttonLabel="Start monitoring"
            buttonClass="from-primary-500 to-primary-600 hover:shadow-primary-500/25"
            hoverClass="from-primary-600 to-primary-700"
            appBaseUrl={appBaseUrl}
          />
        </div>
      </div>
    </section>
  );
}

function FaqSection({ appBaseUrl }: Pick<MarketingSectionsProps, "appBaseUrl">) {
  return (
    <section id="faq" className="bg-gray-50 py-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <SectionHeader title="Frequently Asked Questions" description="Everything you need to know about ScoutUX" />

        <div className="mt-16 space-y-6">
          {faqs.map((faq) => (
            <details key={faq.question} className="group rounded-2xl border border-gray-200 bg-white shadow-sm">
              <summary className="flex cursor-pointer list-none items-center justify-between rounded-2xl px-8 py-4 transition-colors hover:bg-gray-50">
                <h3 className="text-xl font-bold text-gray-900">{faq.question}</h3>
                <ChevronDownIcon className="h-5 w-5 text-gray-500 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-8 py-4">
                <p className="leading-relaxed text-gray-600">{faq.answer}</p>
              </div>
            </details>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <a
            href={`${appBaseUrl}/domains/new`}
            className="flex items-center rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-primary-600 hover:to-primary-700 hover:shadow-primary-500/25"
          >
            Start free audit
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function MarketingFooter() {
  return (
    <footer className="bg-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <a className="mb-4 block text-center text-gray-400 transition-colors hover:text-white" href="/blog">
          Blog
        </a>
        <p className="text-center text-sm text-gray-400">© {new Date().getFullYear()} ScoutUX</p>
      </div>
    </footer>
  );
}

type PricingCardProps = {
  appBaseUrl: string;
  buttonClass: string;
  buttonLabel: string;
  capacityOptions?: Array<{ pages: string; price: string; websites: string }>;
  description: string;
  features: PricingFeature[];
  hoverClass: string;
  name: string;
  price?: string;
};

function PricingCard({
  appBaseUrl,
  buttonClass,
  buttonLabel,
  capacityOptions,
  description,
  features,
  hoverClass,
  name,
  price,
}: PricingCardProps) {
  return (
    <div className="relative h-full">
      <div className="flex h-full flex-col rounded-3xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-8 shadow-lg">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900">{name}</h3>
          {price ? (
            <div className="mt-4 flex items-center justify-center">
              <span className="text-5xl font-bold text-gray-900">{price}</span>
              {price !== "$0" ? <span className="ml-2 text-xl text-gray-600">/month</span> : null}
            </div>
          ) : null}
          <p className="mt-3 text-sm text-gray-600">{description}</p>
        </div>

        {capacityOptions ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {capacityOptions.map((option) => (
              <div key={option.websites} className="rounded-2xl border border-primary-100 bg-white p-5">
                <p className="text-base font-semibold text-gray-900">{option.websites}</p>
                <p className="mt-1 text-sm text-gray-600">{option.pages}</p>
                <div className="mt-4 flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900">{option.price}</span>
                  <span className="ml-1 text-sm text-gray-600">/month</span>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {capacityOptions ? (
          <p className="mt-8 text-sm font-semibold text-gray-900">Included with both options</p>
        ) : null}
        <div className={`mt-4 flex-1 ${capacityOptions ? "grid gap-4 sm:grid-cols-2" : "space-y-4"}`}>
          {features.map((feature) => (
            <div key={feature.text} className="flex items-start gap-3">
              <PricingFeatureIcon feature={feature} />
              <div>
                <p className="text-base font-semibold text-gray-900">{feature.text}</p>
                {feature.subtitle ? <p className="text-sm text-gray-600">{feature.subtitle}</p> : null}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <a
            href={`${appBaseUrl}/domains/new`}
            className={`group relative block w-full overflow-hidden rounded-xl bg-gradient-to-r px-6 py-4 text-center text-lg font-semibold text-white shadow-xl transition-all duration-300 hover:scale-105 ${buttonClass}`}
          >
            <span className="relative z-10">{buttonLabel}</span>
            <div
              className={`absolute inset-0 bg-gradient-to-r opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${hoverClass}`}
            />
          </a>
          <p className="mt-3 text-center text-sm text-gray-600">
            {name === "Free" ? "No credit card required" : "Cancel anytime"}
          </p>
        </div>
      </div>
    </div>
  );
}

function PricingFeatureIcon({ feature }: { feature: PricingFeature }) {
  const iconClass = "h-3.5 w-3.5 text-white";

  return (
    <div className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${feature.iconBg}`}>
      {feature.icon === "check" ? <CheckIcon className={iconClass} /> : null}
      {feature.icon === "check-circle" ? (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ) : null}
      {feature.icon === "clock" ? <ClockIcon className={iconClass} /> : null}
      {feature.icon === "lightning" ? <LightningIcon className={iconClass} /> : null}
    </div>
  );
}
